// src/api/rpContextApi.ts
//
// H-2C2 WP-6 — the frontend client for the CRM's server-only RP context
// endpoint, `GET /auth/context`.
//
// SAME-ORIGIN call to the CRM backend (BFF model): the browser holds NO
// tokens — the opaque `crm_sid` cookie travels automatically with
// `credentials: 'include'`. The endpoint answers with switch-context-derived
// data only; on failure it answers with the stable recovery contract
// `{ error, recovery, correlationId }` (see server/src/auth/oidc/recovery).
//
// Outcome mapping:
//  - 200 with a `context` → ok.
//  - a failure body carrying a canonical `recovery` → that state verbatim
//    (this is the backend-authoritative path, incl. 503 `central_unavailable`
//    → `degraded`).
//  - a 503 `handoff_oidc_crm_disabled` (the flag-gated `/auth` router answers
//    this until `HANDOFF_OIDC_CRM` is enabled server-side) → `auth_unavailable`
//    — a configuration/availability surface, deliberately NOT `degraded`, so
//    the deploy-window mismatch (frontend flag ON, backend flag OFF) is not
//    mis-reported as a Central outage.
//  - a network error / opaque non-2xx → `degraded` (a genuine transient).

import type { SwitchContextMeta } from '@levelup/central-auth';

import {
  isRecoveryState,
  type RecoveryState,
  type SurfaceState,
} from '../components/auth/recoveryStates';

/**
 * Switch-context-derived runtime context — Central's switch-context payload as
 * normalized by the CRM backend (`SuiteRuntimeContext`). Tokens NEVER cross
 * this boundary. `RpBootGate` adapts this into the `bootContext` store via the
 * shared `mapSwitchContextResponse` normalizer.
 */
export interface RpRuntimeContext {
  user: Record<string, unknown>;
  workspace: Record<string, unknown>;
  apps: unknown[];
  otherWorkspaces: unknown[];
  capabilities: unknown;
  /** From Central `meta.cacheTtlSeconds`; 0 = do not cache. */
  cacheTtlSeconds: number;
  /**
   * Browser-safe Central `meta` (allowlisted server-side; tokens/secrets never
   * cross this boundary). Reuses the shared `@levelup/central-auth`
   * `SwitchContextMeta` contract so the RP boot path carries the SAME meta the
   * Bearer/direct path does — notably `features.uiShell` for shell selection.
   * Optional for backward compatibility with older BFF responses that omit it.
   */
  meta?: SwitchContextMeta;
}

/** The outcome of a `GET /auth/context` call — never a thrown error. */
export type RpContextResult =
  | { ok: true; context: RpRuntimeContext; cacheHit: boolean }
  | { ok: false; recovery: SurfaceState };

/** Marker string in the backend's flag-disabled 503 body. */
const HANDOFF_DISABLED_MARKER = 'handoff_oidc_crm_disabled';

/**
 * Fetch the canonical runtime context from the CRM backend.
 *
 * Never throws — every failure mode (network error, 5xx, handoff disabled,
 * deterministic auth failure, unparseable body) resolves to
 * `{ ok: false, recovery }` carrying a canonical recovery state (or the
 * frontend-only `auth_unavailable`).
 */
/**
 * The member's current access version, or null when it could not be read.
 *
 * Cheap enough to poll — Central answers with one indexed membership lookup,
 * no capability resolution and no caches. OPAQUE: it changed or it did not.
 * Never parsed, never trusted as a description of access.
 *
 * null means "could not tell", NOT "changed". Treating a failed probe as a
 * change would drive a refetch loop against a backend that is already
 * struggling.
 */
export async function fetchAccessVersion(): Promise<string | null> {
  try {
    const res = await fetch('/auth/access-version', {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-store' },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { version?: unknown } | null;
    return typeof body?.version === 'string' ? body.version : null;
  } catch {
    return null;
  }
}

export async function fetchRpContext(
  /**
   * Force a re-resolve instead of accepting the per-session cache, on both
   * this server and Central. Used only after the version poll has already
   * seen a change — otherwise a client that NOTICED one still could not act
   * on it, because both caches would keep serving the pre-change context.
   */
  options: { fresh?: boolean } = {},
): Promise<RpContextResult> {
  let res: Response;
  try {
    res = await fetch(options.fresh ? '/auth/context?fresh=1' : '/auth/context', {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-store' },
    });
  } catch {
    // Network failure — transient, retryable.
    return { ok: false, recovery: 'degraded' };
  }

  // Read the body once as text, then attempt JSON — so a non-JSON disabled
  // response (text/plain) is still inspectable.
  const rawText = await res.text().catch(() => '');
  let body: Record<string, unknown> | null = null;
  if (rawText) {
    try {
      const parsed: unknown = JSON.parse(rawText);
      body = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      body = null;
    }
  }

  if (res.ok) {
    const context = body?.context;
    if (context && typeof context === 'object') {
      return {
        ok: true,
        context: context as RpRuntimeContext,
        cacheHit: body?.cacheHit === true,
      };
    }
    // 200 with an unusable body — fail closed.
    return { ok: false, recovery: 'malformed' };
  }

  // Failure: trust the backend-authoritative `recovery` field when present.
  if (isRecoveryState(body?.recovery)) {
    return { ok: false, recovery: body.recovery as RecoveryState };
  }
  // The flag-gated `/auth` router's disabled 503 — a config/availability
  // condition, surfaced as `auth_unavailable` (never `degraded`).
  if (rawText.includes(HANDOFF_DISABLED_MARKER)) {
    return { ok: false, recovery: 'auth_unavailable' };
  }
  // Any other opaque non-2xx — treat as a transient/degraded condition.
  return { ok: false, recovery: 'degraded' };
}
